import { useState } from "react";

import Alert from "@mui/material/Alert";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Snackbar from "@mui/material/Snackbar";

import useAuth from "hooks/useAuth";
import { GENERAL_ADMIN_SCOPE_VALUE } from "constants/airportScope";

function airportLabel(airport: { code?: string; name?: string; _id: string }) {
  const code = String(airport.code || "").trim();
  const name = String(airport.name || "").trim();
  if (code && name && code !== name) return `${code} — ${name}`;
  return code || name || airport._id;
}

export default function AirportSwitcher({
  compact = false,
}: {
  compact?: boolean;
}) {
  const {
    user,
    availableAirports,
    canSwitchAirport,
    airportSelectionLoading,
    selectAirport,
    selectGeneralScope,
  } = useAuth();
  const [error, setError] = useState<string | null>(null);

  if (!canSwitchAirport) return null;

  const handleChange = async (event: SelectChangeEvent<string>) => {
    const selectedScope = event.target.value;
    if (!selectedScope) return;
    if (selectedScope === GENERAL_ADMIN_SCOPE_VALUE && !user?.airportId) return;
    if (selectedScope === user?.airportId) return;

    setError(null);
    try {
      if (selectedScope === GENERAL_ADMIN_SCOPE_VALUE) {
        await selectGeneralScope();
      } else {
        await selectAirport(selectedScope);
      }
    } catch (switchError) {
      setError(
        switchError instanceof Error
          ? switchError.message
          : "Failed to switch airports.",
      );
    }
  };

  const isSuperAdmin =
    String(user?.positionKey || "").toLowerCase() === "super_admin";
  const currentValue =
    isSuperAdmin && !user?.airportId
      ? GENERAL_ADMIN_SCOPE_VALUE
      : String(user?.airportId || "");
  const currentAirportIsLoaded = availableAirports.some(
    (airport) => airport._id === currentValue,
  );

  return (
    <>
      <FormControl
        size="small"
        sx={{
          minWidth: compact ? 150 : 220,
          maxWidth: compact ? 180 : 280,
          mx: 1,
        }}
      >
        <InputLabel id="global-airport-selector-label">
          {isSuperAdmin ? "Workspace" : "Airport"}
        </InputLabel>
        <Select
          labelId="global-airport-selector-label"
          aria-label="Active airport"
          label={isSuperAdmin ? "Workspace" : "Airport"}
          value={currentValue}
          onChange={(event) => void handleChange(event)}
          disabled={airportSelectionLoading}
        >
          {isSuperAdmin ? (
            <MenuItem value={GENERAL_ADMIN_SCOPE_VALUE}>
              General administration
            </MenuItem>
          ) : null}
          {currentValue &&
          currentValue !== GENERAL_ADMIN_SCOPE_VALUE &&
          !currentAirportIsLoaded ? (
            <MenuItem value={currentValue}>
              {user?.airportName || currentValue}
            </MenuItem>
          ) : null}
          {availableAirports.map((airport) => (
            <MenuItem key={airport._id} value={airport._id}>
              {airportLabel(airport)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Snackbar
        open={Boolean(error)}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
}
